import { useCallback } from "react";
import { ApolloClient, useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { GET_MEMBER_SHOPPING_CART } from "../graphql/resolvers/member";
import { GET_LOCAL_SHOPPING_CART } from "../graphql/resolvers/cart";
import { MemberShoppingCart } from "../types/__generated__/MemberShoppingCart";
import { LocalShoppingCart } from "../types/__generated__/LocalShoppingCart";
import { showErrorAlert } from "./alerts";
import { isLoggedIn } from "./auth";
import { I18N_NAMESPACES } from "./constants";
import {
  countCartWineBottles,
  getBuildYourOwnApplied,
  getBuildYourOwnIntent,
  getBuildYourOwnProgress,
  getBuildYourOwnSetAside,
  isWineProduct,
} from "./buildYourOwn";

type LockClient = Pick<ApolloClient<object>, "cache">;

/** The order is a build-your-own calendar: wines only, and only this many of them. */
export interface BuildYourOwnCartLock {
  /** Wines the calendar takes; unbounded when the code is set aside and the grant is not on the cart. */
  perNumberOfWines: number;
  /** Paid wine bottles in the cart right now. */
  wineBottles: number;
}

const readCached = <T>(client: LockClient, query: typeof GET_MEMBER_SHOPPING_CART): T | null => {
  try {
    return client.cache.readQuery<T>({ query, returnPartialData: true });
  } catch {
    return null;
  }
};

/**
 * Whether this visitor's cart is a build-your-own advent order right now, and
 * how much room the calendar has left. Read at click time from the local
 * markers and the cached carts (every cart mutation refetches the member's),
 * so the many add buttons on a listing page need no query of their own.
 *
 * A member's cart may be cached only in part — a page that fetched its basics
 * and nothing else — or hold a code the backend set aside; neither says
 * whether the order is the calendar's, so the marker written when the member
 * applied the code decides, with the calendar's size it keeps. The wines in
 * the cart count only as far as they have been read.
 */
export const readBuildYourOwnCartLock = (client: LockClient): BuildYourOwnCartLock | null => {
  if (!isLoggedIn()) {
    const intent = getBuildYourOwnIntent();
    if (!intent) return null;
    const localCart = readCached<LocalShoppingCart>(client, GET_LOCAL_SHOPPING_CART)?.localShoppingCart;
    return { perNumberOfWines: intent.perNumberOfWines, wineBottles: countCartWineBottles(localCart?.items) };
  }
  const applied = getBuildYourOwnApplied();
  const cart = readCached<MemberShoppingCart>(client, GET_MEMBER_SHOPPING_CART)?.me?.shoppingCart;
  const wineBottles = cart?.shoppingcartitemSet ? countCartWineBottles(cart.shoppingcartitemSet) : 0;
  const isCartComplete =
    !!cart && cart.promoCodeFreebies !== undefined && cart.shoppingcartitemSet !== undefined;
  if (!isCartComplete) {
    return applied ? { perNumberOfWines: applied.perNumberOfWines ?? Infinity, wineBottles } : null;
  }
  const progress = getBuildYourOwnProgress(cart.promoCodeFreebies);
  if (progress) return { perNumberOfWines: progress.perNumberOfWines, wineBottles };
  const { isSetAside } = getBuildYourOwnSetAside({
    appliedPromoCode: applied?.promoCode ?? null,
    discountCode: cart.discountCode,
    grants: cart.promoCodeFreebies,
    items: cart.shoppingcartitemSet,
  });
  return isSetAside ? { perNumberOfWines: applied?.perNumberOfWines ?? Infinity, wineBottles } : null;
};

/*
 * Wine allowed by the guard but not yet in the cached cart. Two add controls
 * clicked before either mutation's refetch lands would otherwise both see the
 * same room; each allowed add reserves its wine until the cached count has
 * caught up with it (or, failing that, until it ages out).
 */
interface Reservation {
  quantity: number;
  /** The cached wine count that shows this add has landed. */
  expectedCount: number;
  at: number;
}

const RESERVATION_TTL_MS = 10_000;
let reservations: Reservation[] = [];

const pendingWine = (cachedCount: number, now: number): number => {
  reservations = reservations.filter(
    (reservation) => now - reservation.at < RESERVATION_TTL_MS && cachedCount < reservation.expectedCount
  );
  return reservations.reduce((total, reservation) => total + reservation.quantity, 0);
};

/** For tests: forget any wine reserved by earlier guarded adds. */
export const resetBuildYourOwnReservations = () => {
  reservations = [];
};

const wineQuantityOf = (lines: AddableLine[]): number =>
  lines.reduce(
    (total, line) => total + (isWineProduct(line.product) ? Math.max(line.quantity ?? 1, 0) : 0),
    0
  );

/**
 * Hands back the wine an allowed add reserved, when that add then failed: the
 * cart never gained it, so an immediate retry must see the room again rather
 * than wait for the reservation to age out. The newest reservation for the
 * same amount is the one released; nothing happens if there is none.
 */
export const releaseBuildYourOwnReservation = (lines: AddableLine[]) => {
  const quantity = wineQuantityOf(lines);
  if (quantity <= 0) return;
  for (let index = reservations.length - 1; index >= 0; index -= 1) {
    if (reservations[index].quantity === quantity) {
      reservations.splice(index, 1);
      return;
    }
  }
};

export interface AddableLine {
  product: { productType?: { id: number | null } | null } | null | undefined;
  quantity?: number;
}

/**
 * A guard for add-to-cart actions. Returns true, having shown the reason, when
 * the lines cannot join a build-your-own order — anything that isn't wine, or
 * more wine than the one calendar has room for; false when the add may go
 * ahead (and always when no calendar is in progress).
 */
export const useBuildYourOwnAddGuard = () => {
  const client = useApolloClient();
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  return useCallback(
    (lines: AddableLine[]): boolean => {
      const lock = readBuildYourOwnCartLock(client);
      if (!lock) return false;
      if (!lines.every((line) => isWineProduct(line.product))) {
        showErrorAlert(t("common:buildYourOwn.onlyWines"), { autoClose: 10000 });
        return true;
      }
      if (!Number.isFinite(lock.perNumberOfWines)) return false;
      const adding = lines.reduce((total, line) => total + Math.max(line.quantity ?? 1, 0), 0);
      const now = Date.now();
      const pending = pendingWine(lock.wineBottles, now);
      const room = lock.perNumberOfWines - lock.wineBottles - pending;
      if (adding <= room) {
        reservations.push({ quantity: adding, expectedCount: lock.wineBottles + pending + adding, at: now });
        return false;
      }
      showErrorAlert(
        room > 0
          ? t("common:buildYourOwn.calendarRoom", { count: room })
          : t("common:buildYourOwn.calendarFull", { perNumberOfWines: lock.perNumberOfWines }),
        { autoClose: 10000 }
      );
      return true;
    },
    [client, t]
  );
};
